import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { MdWarningAmber } from "react-icons/md";
import Modal from "../Components/Common/ui/Modal";
import Button from "../Components/Common/ui/Button";

const ConfirmContext = createContext();

// Promise-based replacement for window.confirm(): const ok = await confirm({...}); if (!ok) return;
// Renders through the shared Modal/Button primitives so it matches app styling
// instead of raw browser chrome (see "pos.charithkariyawasam.com says" native dialogs).
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    if (typeof opts === "string") opts = { message: opts };
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const settle = (result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setOptions(null);
  };

  const {
    title = "Are you sure?",
    message = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    danger = false,
  } = options || {};

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!options}
        onClose={() => settle(false)}
        title={title}
        maxWidthClassName="max-w-[420px]"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {cancelText}
            </Button>
            <Button variant={danger ? "danger" : "primary"} onClick={() => settle(true)} autoFocus>
              {confirmText}
            </Button>
          </>
        }
      >
        <div className="flex gap-3">
          {danger && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-500/10 dark:text-rose-300">
              <MdWarningAmber className="text-xl" aria-hidden="true" />
            </div>
          )}
          {message && <p className="m-0 text-sm text-slate-600 dark:text-slate-300">{message}</p>}
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
};
